// Placed here for now. Move component to suitable place in the future.

import React, { useState } from 'react'

import { RichText } from 'components_new/molecules/RichTextEditor/types'
import { SxProps } from '@mui/material'

import { useReportEditions } from 'redux/hooks/Reports'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Skeleton from 'components_new/atoms/Skeleton'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import RichTextEditor from 'components_new/molecules/RichTextEditor'
import SelectButtonBase from 'components_new/molecules/SelectButtonBase'
import SearchAndSelectPopover from 'components_new/molecules/SearchAndSelectPopover'
import StatusBox from 'components_new/molecules/StatusBox'

interface RichTextContentProps {
  editionId: string | null
  editMode: boolean
  onBlur?: (value: RichText | null) => void
  reportId: string
  sx?: SxProps
  value: RichText | null
  variant: 'edition' | 'template'
  widgetBlockId: string | null
  widgetId: string
}

const RichTextContent = (props: RichTextContentProps) => {
  const {
    editionId,
    editMode,
    onBlur,
    reportId,
    sx,
    value,
    variant,
    widgetBlockId,
    widgetId
  } = props

  const allEditions = useReportEditions(reportId)
  const editions = allEditions.filter((e) => e.id !== editionId)
  const { t } = useTranslation()

  const [insertValue, setInsertValue] = useState<RichText | null | undefined>(
    undefined
  )

  const handleInsertFromEdition = (selectedEditionId: string | null) => {
    if (!selectedEditionId) {
      setInsertValue(null)
      onBlur?.(null)
    }

    // Find the template block ID by looking up the current edition's block
    const currentEdition = allEditions.find((e) => e.id === editionId)
    const currentBlock = currentEdition?.blocks.find(
      (b) => b.id === widgetBlockId
    )

    const widgetIndex =
      currentBlock?.widgets.findIndex((w) => w.id === widgetId) ?? 0

    const targetEdition = editions.find((e) => e.id === selectedEditionId)
    const targetBlock = targetEdition?.blocks.find(
      (b) => b.report_block_id === currentBlock?.report_block_id
    )

    const targetWidget = targetBlock?.widgets[widgetIndex] ?? null
    const richText = targetWidget?.rich_text ?? null

    setInsertValue(richText)
    onBlur?.(richText)
  }

  if (variant === 'template')
    return (
      <Text variant="body1">
        <Skeleton animation={false} sx={{ mb: '0.5em' }} width="50%" />
        <Skeleton animation={false} sx={{ mb: '0.5em' }} width="50%" />
        <Skeleton animation={false} width="25%" />
      </Text>
    )

  /*-- edition --*/
  return (
    <Box sx={{ flex: 1, minWidth: 0, ...sx }}>
      <RichTextEditor
        actions={
          <>
            <Tooltip
              title={
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  {t('report_edition', 'generate_text_from_ai')}
                  <Chip
                    color="secondary"
                    label={t('global', 'coming_soon')}
                    size="xsmall"
                  />
                </Box>
              }
            >
              <IconButton
                size="small"
                sx={{
                  cursor: 'not-allowed',
                  ':hover': { color: 'secondary.main' }
                }}
              >
                <Icon fontSize="small" name="AI" />
              </IconButton>
            </Tooltip>

            <SearchAndSelectPopover
              multiple={false}
              onChange={handleInsertFromEdition}
              options={editions.map((edition) => {
                return {
                  avatar: 'report',
                  id: edition.id,
                  title: edition.title ?? ''
                }
              })}
              placeholder={`${t('global', 'search')} ${t(
                'global',
                'edition',
                true
              )}`}
              popoverContent={
                <StatusBox
                  severity="info"
                  size="small"
                  title={t('report_edition', 'previously_used_text_field')}
                >
                  {t('report_edition', 'insert_text_from_previous_edition')}
                </StatusBox>
              }
              value={null}
            >
              <SelectButtonBase
                placeholder={t(
                  'report_edition',
                  'insert_text_from_placeholder'
                )}
                value={null}
              />
            </SearchAndSelectPopover>
          </>
        }
        contentMaxWidth="content"
        edit={editMode}
        editHeight={96}
        onBlur={onBlur}
        settings={{
          bold: true,
          headingTwo: true,
          headingThree: true,
          italic: true,
          underline: true,
          bulletedList: true,
          numberedList: true,
          code: true
        }}
        sx={{ minWidth: 0 }}
        value={insertValue !== undefined ? insertValue : value}
      />
    </Box>
  )
}

export default RichTextContent
